import React, { useEffect, useRef, useState } from "react";
import { StyledContainer } from "./styles";
import SVG from "react-inlinesvg";
import { WRITE_MODE_AUTO, WRITE_MODE_GUIDED, WRITE_MODE_TONE } from "../../../../src/shared/constants";
import classnames from "classnames";
import WriteSettings from "../WriteSettings";

export default function WriteMenu({
  setWriteOpen,
  setWriteMode,
  writeMode,
  tones,
  tone,
  setTone,
  editorOptions,
  setEditorOptions,
  wormholeOutputLength,
  wormholeOutputCards,
  creativitySliderPosition,
  memory,
  setMemory,
  memoryEnabled,
  setMemoryEnabled,
  openGuidance,
  writeModel,
  setModalOpen,
  metadata,
  setMetadata,
}) {
  const wrapperRef = useRef(null);

  const [settingsOpen, setSettingsOpen] = useState(false);

  // Focus first menu item when menu opens
  useEffect(() => {
    if (wrapperRef.current) {
      requestAnimationFrame(() => {
        const firstItem = wrapperRef.current?.querySelector('li[role="menuitem"]');
        if (firstItem) firstItem.focus();
      });
    }
  }, []);

  return (
    <div
      ref={wrapperRef}
    >
      <StyledContainer data-testid="write-options-menu">
        {settingsOpen && (
          <WriteSettings
            initialWormholeOutputLength={wormholeOutputLength}
            initialWormholeOutputCards={wormholeOutputCards}
            initialCreativitySliderPosition={creativitySliderPosition}
            initialWriteModel={writeModel}
            editorOptions={editorOptions}
            setEditorOptions={setEditorOptions}
            setSettingsOpen={setSettingsOpen}
            initialMemory={memory}
            setProjectMemory={setMemory}
            memoryEnabled={memoryEnabled}
            setMemoryEnabled={setMemoryEnabled}
            setModalOpen={setModalOpen}
            metadata={metadata}
            setMetadata={setMetadata}
          />
        )}
        <ul role="menu" aria-hidden={settingsOpen || undefined} style={settingsOpen ? { display: "none" } : undefined}>
          <li
            role="menuitem"
            tabIndex={settingsOpen ? -1 : 0}
            onClick={(e) => {
              setWriteMode(WRITE_MODE_AUTO);
              setWriteOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setWriteMode(WRITE_MODE_AUTO);
                setWriteOpen(false);
              }
            }}
            className={classnames({ open: writeMode === WRITE_MODE_AUTO })}
          >
            <div className="icon-container">
              <SVG src="/assets/auto-sparkle.svg" />
            </div>
            <div className={classnames("column", "label")}>
              <h3>Auto</h3>
              <p>Autocomplete plus magic</p>
            </div>
          </li>
          <li
            role="menuitem"
            tabIndex={settingsOpen ? -1 : 0}
            onClick={(e) => {
              setWriteMode(WRITE_MODE_GUIDED);
              setWriteOpen(false);
              openGuidance();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setWriteMode(WRITE_MODE_GUIDED);
                setWriteOpen(false);
                openGuidance();
              }
            }}
            className={classnames({ open: writeMode === WRITE_MODE_GUIDED })}
          >
            <div className="icon-container">
              <SVG src="/assets/guided-arrows.svg" />
            </div>
            <div className={classnames("column", "label")}>
              <h3>Guided</h3>
              <p>Follows your instructions</p>
            </div>
          </li>
          <li
            role="menuitem"
            tabIndex={settingsOpen ? -1 : 0}
            onClick={(e) => {
              setWriteMode(WRITE_MODE_TONE);
            }}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
                e.preventDefault();
                setWriteMode(WRITE_MODE_TONE);
              }
            }}
            className={classnames({ open: writeMode === WRITE_MODE_TONE })}
          >
            <div className="column">
              <div className="row">
                <div className="icon-container">
                  <SVG src="/assets/tone.svg" />
                </div>
                <div className={classnames("column", "label")}>
                  <h3>Tone Shift</h3>
                  <p>Changes the style of your writing</p>
                </div>
              </div>

              <section className={classnames("tones", { selected: writeMode === WRITE_MODE_TONE })}>
                {writeMode === WRITE_MODE_TONE &&
                  tones.map((t) => {
                    return (
                      <button
                        type="button"
                        key={t.value}
                        className={classnames("use-component-styles tone-shift-tone", { selected: tone.value === t.value })}
                        onClick={(e) => {
                          e.stopPropagation();
                          setTone(t);
                          setWriteOpen(false);
                        }}
                      >
                        {t.label}
                      </button>
                    );
                  })}
              </section>
            </div>
          </li>
          <li
            role="menuitem"
            tabIndex={settingsOpen ? -1 : 0}
            onClick={(e) => {
              setSettingsOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setSettingsOpen(true);
              }
            }}
          >
            <div className="icon-container">
              <SVG
                width={25}
                height={25}
                src="/assets/settings.svg"
              />
            </div>
            <div className={classnames("column", "label")}>
              <h3>Write settings</h3>
              {memory && memory.length > 0 && memoryEnabled && <p>Key details: on</p>}
            </div>
          </li>
        </ul>
      </StyledContainer>
    </div>
  );
}
